import React, { useState, useEffect, useMemo } from 'react';
import axiosInstance from './backend/axiosInstance';
import "./MatchHistory.css";
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { LeagueIconComponent } from './data/LeagueIcons';
import { TeamIcons, TeamIconsMeta } from './data/TeamIcons';
import { AgentIcons } from './data/AgentIcons';
import { Maps } from './data/MapIcons';
import AgentCustomSelect from './selects/AgentCustomSelect';
import MapCustomSelect from './selects/MapCustomSelect';
import TeamCustomSelect from './selects/TeamCustomSelect';
import PatchCustomSelect from './selects/PatchCustomSelect';
import LeagueCustomSelect from './selects/LeagueCustomSelect';


function MatchHistory() {
    document.title = "Match History";

    const [matchData, setMatchData] = useState([]);
    const LOAD_STEP = 15;
    const [visibleCount, setVisibleCount] = useState(LOAD_STEP);

    const [selectedAgents, setSelectedAgents] = useState([]);
    const [filterAgents, setFilterAgents] = useState([]);

    const [selectedMaps, setSelectedMaps] = useState([]);
    const [filterMaps, setFilterMaps] = useState([]);

    const [selectedPatch, setSelectedPatch] = useState([]);
    const [filterPatch, setFilterPatch] = useState([]);
    const [patchOption, setPatchOption] = useState([]);

    const [selectedTeam, setSelectedTeam] = useState([]);
    const [filterTeam, setFilterTeam] = useState([]);
    const [teamOption, setTeamOption] = useState([]);

    const [selectedLeague, setSelectedLeague] = useState([]);
    const [filterLeague, setFilterLeague] = useState([]);
    const [leagueOption, setLeagueOption] = useState([]);

    const handleAgentChange = (selectedOptions) => {
        const agentValues = selectedOptions.map((option) => option.value);
        setFilterAgents(agentValues);
        setSelectedAgents(selectedOptions);
    };

    const handleMapChange = (selectedOptions) => {
        const mapValues = selectedOptions.map((option) => option.value);
        setFilterMaps(mapValues);
        setSelectedMaps(selectedOptions);
    };

    const handleTeamChange = (selectedOptions) => {
        const leagueValues = selectedOptions.map((option) => option.value);
        setFilterTeam(leagueValues);
        setSelectedTeam(selectedOptions);
    };

    const handlePatchChange = (selectedOptions) => {
        const leagueValues = selectedOptions.map((option) => option.value);
        setFilterPatch(leagueValues);
        setSelectedPatch(selectedOptions);
    };

    const handleLeagueChange = (selectedOptions) => {
        const leagueValues = selectedOptions.map((option) => option.value);
        setFilterLeague(leagueValues);
        setSelectedLeague(selectedOptions);
    };

    const getUniqueTeams = (matchData) => {
        const teamsSet = new Set();
        matchData.forEach((match) => {
            teamsSet.add(match.team1);
            teamsSet.add(match.team2);
        });
        return [...teamsSet];
    };

    const handleClickMatch = (gameId) => {
        window.open(`/match/${gameId}`, '_blank');
    };

    useEffect(() => {
        axiosInstance.get('matches/')
            .then((response) => {
                setMatchData(response.data);


                const uniquePatches = [...new Set(response.data.map((m) => m.patch))];
                const uniqueTeams = getUniqueTeams(response.data);
                const uniqueLeagues = [...new Set(response.data.map((m) => m.leagueName))].filter(Boolean);


                const formattedPatchOptions = uniquePatches
                    .sort((a, b) => {
                        const [a1, a2] = a.split(".").map(Number);
                        const [b1, b2] = b.split(".").map(Number);
                        return a1 === b1 ? a2 - b2 : a1 - b1;
                    })
                    .map((p) => ({ value: p, label: p }));

                const formattedTeamOptions = uniqueTeams
                    .sort((a, b) => a.localeCompare(b))
                    .map((t) => ({ value: t, label: t }));

                const formattedLeagueOptions = uniqueLeagues
                    .sort()
                    .map((l) => ({ value: l, label: l }));
                setPatchOption(formattedPatchOptions);
                setTeamOption(formattedTeamOptions);
                setLeagueOption(formattedLeagueOptions);

            })

    }, []);


    const filteredMatches = useMemo(() => {
        return matchData.filter((match) => {
            const isLeagueMatch =
                filterLeague.length === 0 || filterLeague.includes(match.leagueName);

            const isPatchMatch =
                filterPatch.length === 0 || filterPatch.includes(match.patch);

            const isTeamMatch =
                filterTeam.length === 0 ||
                filterTeam.includes(match.team1) ||
                filterTeam.includes(match.team2);

            const matchChampions = [
                match.blue1, match.blue2, match.blue3, match.blue4, match.blue5,
                match.red1, match.red2, match.red3, match.red4, match.red5
            ].filter(Boolean);

            const isMapMatch =
                filterMaps.length === 0 ||
                filterMaps.includes(match.map);

            const isChampionMatch =
                filterAgents.length === 0 ||
                filterAgents.every(id =>
                    matchChampions.includes(id)
                );

            return isMapMatch && isLeagueMatch && isPatchMatch && isTeamMatch && isChampionMatch;
        });
    }, [matchData, filterMaps, filterPatch, filterTeam, filterAgents, filterLeague]);


    const tbodyVariants = {
        hidden: {},
        visible: {
            transition: {
                staggerChildren: 0.05,
            },
        },
    };


    return (
        <div style={{ display: 'flex', flexDirection: "column", alignItems: "center" }}>

            <div className="filters">
                <div className='short-chooser'>
                    <LeagueCustomSelect options={leagueOption} selectedOption={selectedLeague} onOptionChange={handleLeagueChange} />
                </div>
                <div className='short-chooser'>
                    <TeamCustomSelect options={teamOption} selectedOption={selectedTeam} onOptionChange={handleTeamChange} />
                </div>
                <div className='short-chooser'>
                    <PatchCustomSelect options={patchOption} selectedOption={selectedPatch} onOptionChange={handlePatchChange} />
                </div>
                <div className='short-chooser'>
                    <AgentCustomSelect selectedOption={selectedAgents} onOptionChange={handleAgentChange} />
                </div>
                <div className='short-chooser'>
                    <MapCustomSelect selectedOption={selectedMaps} onOptionChange={handleMapChange} />
                </div>
            </div>

            <div className='main-content'>
                <table style={{ width: "100%", border: "0", borderCollapse: "collapse" }}>
                    <thead style={{ backgroundColor: '#1a1a1a', color: "white" }}>
                        <tr>
                            <th style={{ width: "70px", textAlign: "center", borderBottom: "4px solid black" }}></th>
                            <th style={{ width: "140px", textAlign: "center", borderBottom: "4px solid black" }}>League</th>
                            <th style={{ width: "140px", textAlign: "center", borderBottom: "4px solid black" }}>Date</th>
                            <th style={{ width: "120px", textAlign: "center", borderBottom: "4px solid black" }}>Game N</th>
                            <th style={{ width: "120px", textAlign: "center", borderBottom: "4px solid black" }}>Patch</th>
                            <th style={{ width: "240px", textAlign: "center", borderBottom: "4px solid black" }}></th>
                            <th style={{ width: "140px", textAlign: "center", borderBottom: "4px solid black" }}>Team1</th>
                            <th style={{ textAlign: "center", borderBottom: "4px solid black" }}>Result</th>
                            <th style={{ width: "140px", textAlign: "center", borderBottom: "4px solid black" }}>Team2</th>
                            <th style={{ width: "240px", textAlign: "center", borderBottom: "4px solid black" }}> </th>
                            <th style={{ width: "100px", textAlign: "center", borderBottom: "4px solid black" }}>Map</th>
                            <th style={{ width: "180px", textAlign: "center", borderBottom: "4px solid black" }}>Actions</th>
                        </tr>
                    </thead>
                    <motion.tbody
                        variants={tbodyVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {filteredMatches.slice(0, visibleCount).map((match, index) => {

                            return (
                                <motion.tr
                                    key={match.id}
                                    initial={{ opacity: 0, x: -120 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true, amount: 0.2 }}
                                    transition={{
                                        duration: 0.5,
                                        ease: "easeOut",
                                        delay: (index % LOAD_STEP) * 0.05
                                    }}
                                    className='match-history-row'
                                >
                                    <td style={{ textAlign: "center" }}>{index + 1}</td>
                                    <td style={{ textAlign: "center" }}>
                                        <div
                                            style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                gap: "6px",
                                            }}
                                        >
                                            <LeagueIconComponent leagueName={match.leagueName} />
                                            <span>{match.leagueName}</span>
                                        </div>
                                    </td>
                                    <td style={{ textAlign: "center" }}>
                                        {(() => {
                                            const date = new Date(match.date);
                                            const time = date.toLocaleTimeString('en-GB', {
                                                hour: '2-digit',
                                                minute: '2-digit',
                                            });
                                            const dayMonth = date.toLocaleDateString('en-GB', {
                                                day: 'numeric',
                                                month: 'short',
                                            });
                                            return `${time} ${dayMonth}`;
                                        })()}
                                    </td>
                                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                        Game {match.id.split('_')[1]}
                                    </td>
                                    <td style={{ textAlign: "center" }}>{match.patch}</td>
                                    <td style={{ textAlign: "center" }}>
                                        <div
                                            style={{ textAlign: "center", display: "flex", justifyContent: 'center' }}>
                                            {["blue1", "blue2", "blue3", "blue4", "blue5"].map((blueIndex) => (

                                                <div style={{ display: 'flex', flexDirection: "column", alignItems: 'center' }}>
                                                    <img key={blueIndex} src={AgentIcons[match[blueIndex]]} alt={match[blueIndex]} className="small-image" />
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                        <Link to={`/team/${match.team1}`} className="team-link">
                                            {match.team1}
                                            <img src={TeamIcons[match.team1]} style={{ filter: TeamIconsMeta[match.team1]?.dark ? "invert(1)" : "none" }} alt='' className="small-image" />
                                        </Link>
                                    </td>
                                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                        {match.winner === 1 ? "1" : "0"} : {match.winner === 1 ? "0" : "1"}
                                    </td>
                                    <td style={{ textAlign: "center", fontWeight: "bold" }}>
                                        <Link to={`/team/${match.team2}`} className="team-link" >
                                            <img src={TeamIcons[match.team2]} style={{ filter: TeamIconsMeta[match.team2]?.dark ? "invert(1)" : "none" }} alt='' className="small-image" />
                                            {match.team2}
                                        </Link>
                                    </td>

                                    <td style={{ textAlign: "center", position: "relative", zIndex: 20 }}>
                                        <div
                                            style={{ textAlign: "center", display: "flex", justifyContent: 'center', position: "relative" }}>
                                            {["red1", "red2", "red3", "red4", "red5"].map((redIndex) => (
                                                <div style={{ display: 'flex', flexDirection: "column", alignItems: 'center' }}>
                                                    <img key={redIndex} src={AgentIcons[match[redIndex]]} alt={match[redIndex]} className="small-image" />
                                                </div>
                                            ))}
                                        </div>
                                    </td>
                                    <td>
                                        <div
                                            style={{
                                                position: "relative",
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                width: "100px",
                                                height: "50px",
                                            }}
                                        >
                                            <img
                                                src={Maps[match.map]?.splash}
                                                alt={match.map}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                    border: "1px solid white",
                                                    objectFit: "cover",
                                                }}
                                            />

                                            <div
                                                style={{
                                                    position: "absolute",
                                                    color: "white",
                                                    fontWeight: "bold",
                                                    textShadow: "0 0 5px black",
                                                    fontSize: "12px",
                                                }}
                                            >
                                                {match.map}
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <div style={{ display: 'flex', position: 'relative', justifyContent: "space-evenly", gap: "5px", padding: "5px", fontSize: '10px' }}>
                                            <a
                                                href={`/match/${match.id}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="button-dark-mini-2"
                                                style={{ textDecoration: "none" }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleClickMatch(match.id)
                                                }}
                                            >
                                                DETAILS
                                            </a>
                                        </div>
                                    </td>
                                </motion.tr>
                            );
                        })}
                    </motion.tbody>
                </table>
            </div>

        </div>

    );
}



export default MatchHistory;
